import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Flex,
    Button
} from "@chakra-ui/react";
import {
    FaEdit,
    FaEye,
    FaTrashAlt,
} from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { agentsService } from "services/agentsService";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { ROUTES } from "constants/routes-path";

function AgentsList() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useState({orderBy: "codigo"});
    const [response, setResponse] = useState(undefined);
    const dialogRef = useRef(undefined);
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const [agentToDelete, setAgentToDelete] = useState(null);

    const columns = useMemo(() => [
        {
            Header: t("settings.agent.list.table.header.registration"),
            accessor: "codigo",
        },
        {
            Header: t("settings.agent.list.table.header.cpfcnpjperson"),
            accessor: "cpf_cnpj",
            id: "id_pessoa.cpf_cnpj"
        },
        {
            Header: t("settings.agent.list.table.header.name"),
            accessor: "nome",
            id: "id_pessoa.nome"
        },
        {
            Header: t('settings.agent.list.table.header.actions'),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.details'),
                    onClick: (row) => navigate(ROUTES.agentsDetailsPath.replace(":id", row.original.id)),
                    icon: <FaEye />
                },
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => navigate(ROUTES.agentsEditPath.replace(":id", row.original.id)),
                    icon: <FaEdit />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => { handleRemove(row.original.id) },
                    icon: <FaTrashAlt color="#E53E3E" />,
                }
            ]
        }
    ], []);

    const fetchData = async (params = {}) => {
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await agentsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleRemove = (agentId) => {
        setAgentToDelete(agentId);
        dialogRef.current?.displayDialog({
            title: t("settings.agent.list.actions.remove-agent"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmRemove = async () => {
        if (agentToDelete) {
            try {
                await agentsService.delete(agentToDelete);
                showToast({ title: t('settings.agent.messages.success.delete'), status: "success" });
                fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Button
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: undefined }}
                    onClick={() => navigate(ROUTES.agentsAddPath)}
                >
                    {t("settings.agent.new.button")}
                </Button>
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("settings.agent.title")} subtitle={t("settings.agent.description")} />
                <CardBody>
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "codigo", ...searchParams })}
                            onChange={fetchData}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default AgentsList;
